import React, { useState } from 'react';
import { FiX, FiPackage } from 'react-icons/fi';
import axios from 'axios';
import './restockModal.css';

const unitMultipliers = { piece: 1, set: 6, dozen: 12, kg: 1 };
const units = [
  { value: 'piece', label: 'Piece (1)' },
  { value: 'set', label: 'Set (6)' },
  { value: 'dozen', label: 'Dozen (12)' },
  { value: 'kg', label: 'Kg' }
];

const RestockModal = ({ item, suppliers, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    quantity: '',
    unit: item.unit || 'piece',
    costPrice: '',
    transportCost: '',
    sellingPrice: '',
    supplierId: item.supplier_id || '',
    purchaseDate: new Date().toISOString().split('T')[0]
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.quantity || isNaN(formData.quantity) || parseInt(formData.quantity) <= 0) {
      newErrors.quantity = 'Enter a quantity greater than 0';
    }
    if (!formData.costPrice || isNaN(formData.costPrice) || parseFloat(formData.costPrice) < 0) {
      newErrors.costPrice = 'Enter a valid cost price';
    }
    if (formData.transportCost && (isNaN(formData.transportCost) || parseFloat(formData.transportCost) < 0)) {
      newErrors.transportCost = 'Enter a valid transport cost';
    }
    if (formData.sellingPrice && (isNaN(formData.sellingPrice) || parseFloat(formData.sellingPrice) < 0)) {
      newErrors.sellingPrice = 'Enter a valid selling price';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError(null);
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await axios.post(`/items/${item.item_id}/restock`, {
        quantity: parseInt(formData.quantity),
        unit: formData.unit,
        cost_price: parseFloat(formData.costPrice),
        transport_cost: parseFloat(formData.transportCost) || 0,
        selling_price: formData.sellingPrice ? parseFloat(formData.sellingPrice) : null,
        supplier_id: formData.supplierId || null,
        purchase_date: formData.purchaseDate
      });

      onSuccess();
    } catch (err) {
      console.error('Restock error:', err);
      setApiError(err.response?.data?.error || 'Failed to add stock. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPiecesToAdd = formData.quantity
    ? parseInt(formData.quantity) * unitMultipliers[formData.unit]
    : 0;

  return (
    <div className="restock-modal-overlay" onClick={onClose}>
      <div className="restock-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="restock-modal-header">
          <h2>
            <FiPackage /> Add Stock — {item.item_name}
          </h2>
          <button className="restock-close-btn" onClick={onClose} disabled={isSubmitting}>
            <FiX />
          </button>
        </div>

        {apiError && <div className="restock-api-error">{apiError}</div>}

        <form onSubmit={handleSubmit} className="restock-form">
          <div className="restock-current-stock">
            Current stock: <strong>{item.quantity} pcs</strong>
          </div>

          <div className="restock-form-row">
            <div className={`form-group ${errors.quantity ? 'has-error' : ''}`}>
              <label>Quantity Received *</label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleChange}
                min="1"
                placeholder="0"
                disabled={isSubmitting}
              />
              {errors.quantity && <span className="error-message">{errors.quantity}</span>}
            </div>

            <div className="form-group">
              <label>Unit *</label>
              <select name="unit" value={formData.unit} onChange={handleChange} disabled={isSubmitting}>
                {units.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
            </div>
          </div>

          {formData.quantity && (
            <div className="restock-quantity-hint">
              Adding {totalPiecesToAdd} pcs — new total will be {item.quantity + totalPiecesToAdd} pcs
            </div>
          )}

          <div className="restock-form-row">
            <div className={`form-group ${errors.costPrice ? 'has-error' : ''}`}>
              <label>Cost Price (Rs) *</label>
              <input
                type="number"
                name="costPrice"
                value={formData.costPrice}
                onChange={handleChange}
                step="0.01"
                min="0"
                placeholder="0.00"
                disabled={isSubmitting}
              />
              {errors.costPrice && <span className="error-message">{errors.costPrice}</span>}
            </div>

            <div className={`form-group ${errors.transportCost ? 'has-error' : ''}`}>
              <label>Transport Cost (Rs)</label>
              <input
                type="number"
                name="transportCost"
                value={formData.transportCost}
                onChange={handleChange}
                step="1"
                min="0"
                placeholder="0.00"
                disabled={isSubmitting}
              />
              {errors.transportCost && <span className="error-message">{errors.transportCost}</span>}
            </div>
          </div>

          <div className={`form-group ${errors.sellingPrice ? 'has-error' : ''}`}>
            <label>Selling Price (Rs) — optional, leave blank to keep current</label>
            <input
              type="number"
              name="sellingPrice"
              value={formData.sellingPrice}
              onChange={handleChange}
              step="0.01"
              min="0"
              placeholder={`Current: Rs${item.selling_price}`}
              disabled={isSubmitting}
            />
            {errors.sellingPrice && <span className="error-message">{errors.sellingPrice}</span>}
          </div>

          <div className="restock-form-row">
            <div className="form-group">
              <label>Supplier (Optional)</label>
              <select name="supplierId" value={formData.supplierId} onChange={handleChange} disabled={isSubmitting}>
                <option value="">No supplier</option>
                {suppliers.map(s => (
                  <option key={s.supplier_id} value={s.supplier_id}>{s.name}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Purchase Date</label>
              <input
                type="date"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="restock-modal-actions">
            <button type="button" className="cancel-btn" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Adding Stock...' : 'Add Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RestockModal;